/**
 * Absence Insights — confidential dashboard (restricted DWH).
 *
 * Access model: gated to members of the `absence-insights-viewers` RBAC group
 * (driven by the `absence-insights-viewers@finn.com` Google Workspace group) +
 * core admins. Unlike the shared `RequirePermission` guard — which grants
 * everyone while global RBAC is in audit-only mode — this view checks the
 * resolved permission set STRICTLY, so it stays hidden from everyone else even
 * before org-wide enforcement is switched on. The proxy re-authorizes the same
 * permission (`space.people.absence_insights.view`, always-enforced) on the
 * data route, so the data itself is protected regardless of the UI.
 *
 * Renders the department/team-level aggregates the proxy computes from the
 * export model (see proxy-server/routes/absence.js): summary KPIs (with a
 * YoY delta per card), a monthly trend chart (sick leave vs. child care
 * days), and a department/team breakdown sorted by absence rate. No
 * row-level / employee PII is ever fetched here — the "Employees with 10+
 * Days" KPI is computed server-side from a query that never returns
 * employee_id, and it deliberately does NOT respect the Absence Type filter
 * (it's always based on total absence days) since splitting it by type would
 * need two more restricted-table queries for a corner case.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock, ShieldCheck, RefreshCw, AlertCircle } from 'lucide-react';
import { useRBAC } from '../../../contexts/RBACContext';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { ErrorBoundary } from '../../../shared/components/ErrorBoundary';
import AbsenceFilterBar from '../components/AbsenceFilterBar';
import EmployeesTable from '../components/EmployeesTable';
import {
  SummaryCards,
  AbsenceRateTrendChart,
  resolveOrgScopeLevel,
  pickRatePct,
} from '../components/AbsenceInsightsCharts';
import { MonthlyVolumeChart } from '../components/MonthlyVolumeChart';
import { OrgTrendChart } from '../components/AbsenceOrgTrendChart';
import { DayOfWeekChart } from '../components/DayOfWeekChart';
import { TenureChart } from '../components/AbsenceTenureChart';
import { AbsenceRankingList } from '../components/AbsenceRankingList';
import {
  fetchAbsenceInsights,
  fetchAbsenceFilterOptions,
  fetchAbsenceEmployees,
  AbsenceAccessError,
  type AbsenceType,
} from '../services/absenceApi';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

const ABSENCE_VIEW_PERMISSION = 'space.people.absence_insights.view';
// Separate, stricter permission — real names + per-employee monthly data.
// Holding ABSENCE_VIEW_PERMISSION does not imply this one.
const ABSENCE_VIEW_EMPLOYEES_PERMISSION = 'space.people.absence_insights.view_employees';
type Tab = 'overview' | 'employees';

function currentYearMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** January of the current year — the export table only has data from 2026 onward. */
function startOfYear(): string {
  return `${new Date().getFullYear()}-01`;
}

/**
 * Earliest month the Employees tab may ever show, per the 24-month GDPR
 * data-minimisation policy (see EMPLOYEE_VIEW_RETENTION_MONTHS in
 * proxy-server/routes/absence.js, the authoritative enforcement point — this
 * is only a client-side mirror so the picker doesn't even offer an invalid
 * date). 24 months inclusive of the current one, so -23.
 */
function employeeRetentionFloor(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 23);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function AccessRestricted() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
          <Lock className="h-6 w-6 text-zinc-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Access restricted</h2>
          <p className="mt-1 text-sm text-zinc-500">
            The Absence Insights dashboard contains confidential data. Access is limited to the{' '}
            <span className="font-medium">absence-insights-viewers</span> group. If you need access,
            ask a People Space admin.
          </p>
        </div>
      </div>
    </div>
  );
}

function AbsenceInsightsContent({ canViewEmployees }: { canViewEmployees: boolean }) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedSubteams, setSelectedSubteams] = useState<string[]>([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState<string[]>([]);
  const [selectedContractTypes, setSelectedContractTypes] = useState<string[]>([]);
  const [selectedEmployeeStatuses, setSelectedEmployeeStatuses] = useState<string[]>([]);
  const [from, setFrom] = useState(() => startOfYear());
  const [to, setTo] = useState(() => currentYearMonth());
  const [absenceType, setAbsenceType] = useState<AbsenceType>('all');
  const employeeFloor = employeeRetentionFloor();

  const filterOptionsQuery = useQuery({
    queryKey: ['absence-filter-options'],
    queryFn: fetchAbsenceFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  const filterParams = {
    from,
    to,
    departments: selectedDepartments,
    teams: selectedTeams,
    subteams: selectedSubteams,
    supervisors: selectedSupervisors,
    contractTypes: selectedContractTypes,
    employeeStatuses: selectedEmployeeStatuses,
  };

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['absence-insights', filterParams],
    queryFn: () => fetchAbsenceInsights(filterParams),
    retry: (count, err) => !(err instanceof AbsenceAccessError) && count < 2,
  });

  const employeesQuery = useQuery({
    queryKey: ['absence-employees', filterParams],
    queryFn: () => fetchAbsenceEmployees(filterParams),
    enabled: activeTab === 'employees' && canViewEmployees,
    retry: (count, err) => !(err instanceof AbsenceAccessError) && count < 2,
  });

  const handleFromChange = (next: string) => {
    setFrom(next);
    if (next > to) setTo(next);
  };
  const handleToChange = (next: string) => {
    setTo(next);
    if (next < from) setFrom(next);
  };
  const resetFilters = () => {
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedSubteams([]);
    setSelectedSupervisors([]);
    setSelectedContractTypes([]);
    setSelectedEmployeeStatuses([]);
    setFrom(startOfYear());
    setTo(currentYearMonth());
    setAbsenceType('all');
  };

  const filterBar = (
    <AbsenceFilterBar
      departmentOptions={filterOptionsQuery.data?.departments ?? []}
      teamOptions={filterOptionsQuery.data?.teams ?? []}
      subteamOptions={filterOptionsQuery.data?.subteams ?? []}
      supervisorOptions={filterOptionsQuery.data?.supervisors ?? []}
      contractTypeOptions={filterOptionsQuery.data?.contractTypes ?? []}
      employeeStatusOptions={filterOptionsQuery.data?.employeeStatuses ?? []}
      selectedDepartments={selectedDepartments}
      selectedTeams={selectedTeams}
      selectedSubteams={selectedSubteams}
      selectedSupervisors={selectedSupervisors}
      selectedContractTypes={selectedContractTypes}
      selectedEmployeeStatuses={selectedEmployeeStatuses}
      from={from}
      to={to}
      absenceType={absenceType}
      minFrom={activeTab === 'employees' ? employeeFloor : undefined}
      onDepartmentsChange={setSelectedDepartments}
      onTeamsChange={setSelectedTeams}
      onSubteamsChange={setSelectedSubteams}
      onSupervisorsChange={setSelectedSupervisors}
      onContractTypesChange={setSelectedContractTypes}
      onEmployeeStatusesChange={setSelectedEmployeeStatuses}
      onFromChange={handleFromChange}
      onToChange={handleToChange}
      onAbsenceTypeChange={setAbsenceType}
      onReset={resetFilters}
    />
  );

  if (error instanceof AbsenceAccessError) return <AccessRestricted />;

  const scopeLevel = resolveOrgScopeLevel(selectedDepartments, selectedTeams);

  const tabLabels: Record<Tab, string> = { overview: 'Overview', employees: 'Employees' };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    // The Employees tab can't show anything older than the retention floor —
    // clamp up front so the picker never displays a selected date it's about
    // to reject, and so the very first /employees request is already valid.
    if (tab === 'employees' && from < employeeFloor) setFrom(employeeFloor);
  };

  const tabBar = canViewEmployees && (
    <div className="flex gap-1 border-b border-zinc-200">
      {(['overview', 'employees'] as const).map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => handleTabChange(tab)}
          className={`px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === tab
              ? 'border-b-2 border-zinc-900 text-zinc-900'
              : 'text-zinc-400 hover:text-zinc-600'
          }`}
        >
          {tabLabels[tab]}
        </button>
      ))}
    </div>
  );

  let content: React.ReactNode;
  if (activeTab === 'employees') {
    if (employeesQuery.error instanceof AbsenceAccessError) {
      content = <AccessRestricted />;
    } else if (employeesQuery.isPending) {
      content = (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" aria-label="Loading employee absence data" />
        </div>
      );
    } else if (employeesQuery.error) {
      content = (
        <div className="mx-auto mt-6 flex max-w-md items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
          <div className="text-sm text-red-800">
            <p className="font-medium">Couldn&apos;t load employee absence data</p>
            <p className="mt-0.5 text-red-700">
              {employeesQuery.error instanceof Error ? employeesQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </div>
      );
    } else {
      content = (
        <>
          <p className="text-xs text-zinc-400">
            Employee-level data is available for the trailing 24 months only (from{' '}
            {employeesQuery.data.retentionCutoff} onward), per data retention policy.
            {employeesQuery.data.retentionTruncated &&
              ' Your selected date range has been adjusted to stay within this window.'}
          </p>
          <EmployeesTable employees={employeesQuery.data.employees} absenceType={absenceType} />
        </>
      );
    }
  } else if (isPending) {
    content = (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" aria-label="Loading absence insights" />
      </div>
    );
  } else if (error) {
    content = (
      <div className="mx-auto mt-6 flex max-w-md items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        <div className="text-sm text-red-800">
          <p className="font-medium">Couldn&apos;t load absence insights</p>
          <p className="mt-0.5 text-red-700">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  } else {
    content = (
      <>
        <SummaryCards data={data} absenceType={absenceType} />
        <div className="grid gap-6 lg:grid-cols-2">
          <OrgTrendChart
            currentByDept={data.monthlyByDepartment}
            priorByDept={data.priorYear.monthlyByDepartment}
            currentByTeam={data.monthlyByTeam}
            priorByTeam={data.priorYear.monthlyByTeam}
            currentBySubteam={data.monthlyBySubteam}
            priorBySubteam={data.priorYear.monthlyBySubteam}
            scopeLevel={scopeLevel}
            absenceType={absenceType}
          />
          <AbsenceRankingList
            currentByDept={data.monthlyByDepartment}
            priorByDept={data.priorYear.monthlyByDepartment}
            currentByTeam={data.monthlyByTeam}
            priorByTeam={data.priorYear.monthlyByTeam}
            currentBySubteam={data.monthlyBySubteam}
            priorBySubteam={data.priorYear.monthlyBySubteam}
            scopeLevel={scopeLevel}
            absenceType={absenceType}
          />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <AbsenceRateTrendChart
            points={data.monthlyTrend}
            priorPoints={data.priorYear.monthlyTrend}
            absenceType={absenceType}
          />
          <TenureChart
            current={data.byTenure}
            prior={data.priorYear.byTenure}
            companyAverageRatePct={pickRatePct(data.summary, absenceType)}
            absenceType={absenceType}
          />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <MonthlyVolumeChart
            points={data.monthlyTrend}
            priorPoints={data.priorYear.monthlyTrend}
            absenceType={absenceType}
          />
          <DayOfWeekChart current={data.byDayOfWeek} prior={data.priorYear.byDayOfWeek} absenceType={absenceType} />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-zinc-400">
            Source: <code className="rounded bg-zinc-100 px-1 py-0.5">{data.table}</code> · {data.filters.from} to{' '}
            {data.filters.to} · generated {new Date(data.generatedAt).toLocaleString()}
          </p>
          <button
            type="button"
            onClick={() => {
              void logAnalyticsEvent('absence_insights_refresh_clicked', {});
              void refetch();
            }}
            className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="space-y-6">
      {tabBar}
      {filterBar}
      {content}
    </div>
  );
}

export default function AbsenceInsightsView() {
  const { loading, permissions, isCoreAdmin } = useRBAC();

  // STRICT gate — deliberately ignores the audit-only flag so this confidential
  // page stays hidden from non-members even while org-wide RBAC enforcement is off.
  const allowed =
    isCoreAdmin || permissions.has('*') || permissions.has(ABSENCE_VIEW_PERMISSION);
  // Deliberately does NOT fall back to isCoreAdmin — the Employees tab's
  // permission is intentionally excluded from the core_admin role server-side
  // (see lib/rbac.js), so being a core admin does not imply this.
  const canViewEmployees = permissions.has('*') || permissions.has(ABSENCE_VIEW_EMPLOYEES_PERMISSION);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" aria-label="Checking access" />
      </div>
    );
  }

  if (!allowed) return <AccessRestricted />;

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-8">
      <header className="mb-6 flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Absence Insights</h1>
          <p className="text-sm text-zinc-500">
            Confidential · restricted to the absence-insights-viewers group
          </p>
        </div>
      </header>
      <ErrorBoundary fallbackTitle="Couldn't load Absence Insights">
        <AbsenceInsightsContent canViewEmployees={canViewEmployees} />
      </ErrorBoundary>
    </div>
  );
}
